import React, { useState, useEffect, useRef } from "react";
import { IoChevronUpOutline, IoChevronDownOutline, IoCheckmarkOutline } from "react-icons/io5";
import { readableText, lightTint } from "../../utils/themeContrast";
import DotButton from "./DotButton";
import ArrayBar from "./ArrayBar";
import Carousel from "./Carousel";
import produce from "immer";
import styled from "@emotion/styled";
import { ThemeContext, UserContext } from "../../contexts/Context";
import MultiQtyOption from './MultiQtyOption';
import { cloneOptions, getOptionTotalPrice, getOptionUnitPrice } from "../../utils/optionPrice";

const LABEL_ONLY = 0;
const LABEL_DOT = 1;
const LABEL_PRICE = 2;
const LABEL_ONLY_MULTI = 3;
const LABEL_ITEM_ONLY = 4;

const OPTION_BAR = 0;
const OPTION_DOT_SINGLE = 1;
const OPTION_DOT_MULTIPLE = 2;
const OPTION_CAROUSEL = 3;
const OPTION_MULTI_QTY = 4;
const OPTION_SET_MEAL_2 = 5;
const OPTION_SET_MEAL_3 = 6;
const scrollToView = (element) => {
  if (!element) return;
  const behavior = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";

  let scrollContainer = element.parentElement;
  while (scrollContainer) {
    const style = window.getComputedStyle(scrollContainer);
    const canScroll = /(auto|scroll)/.test(style.overflowY) &&
      scrollContainer.scrollHeight > scrollContainer.clientHeight;
    if (canScroll) break;
    scrollContainer = scrollContainer.parentElement;
  }

  if (!scrollContainer) {
    element.scrollIntoView({ behavior, block: "center" });
    return;
  }

  const elementRect = element.getBoundingClientRect();
  const containerRect = scrollContainer.getBoundingClientRect();
  const elementTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
  const targetTop = Math.max(0, elementTop - 16);
  scrollContainer.scrollTo({ top: targetTop, behavior });
}

const MenuOptions = ({
  visibleSlide,
  menuIndex,
  menuSelectionFormRef,
  recommendedArray,
  setRecommendedArray,
  menuOptions,
  setPrice,
  setComparePrice,
  isSetMeal,
  fixedPrice,
  hasRecommended,
  isModal,
  cp,
  showRecItem,
  addRecItem,
  removeRecItem,
  onSelectionChange
}) => {

  const themeContext = React.useContext(ThemeContext);
  const { lanIndex, currencySymbol, displayNetAmount, tax, hidePrice, isLanReverse, company } = React.useContext(UserContext);
  //if item has no codeId, option Types will become normal

  const {
    labelType,
    optionType,
    optionTitle,
    optionTitle2,
    optionPrice,
    optionActive,
    optionRequired,
    isMultiple,
    isReadOnly,
    minQty,
    maxQty,
    options, //Each Option Item Array
  } = menuOptions; //Each option Group

  //State
  const [activeLabel, setActiveLabel] = useState(optionActive);
  const [collapsed, setCollapsed] = useState(false);
  const [optionsState, setOptions] = useState(() => cloneOptions(options));
  const { headerRef, captureSelection } = useOptionAdvance({
    optionsState, minQty, maxQty, isMultiple, optionType, collapsed, setCollapsed,
    enabled: !isReadOnly && activeLabel && !isModal && !hasRecommended
  });
  useEffect(() => {
    if (onSelectionChange) onSelectionChange();
  }, [optionsState, activeLabel, onSelectionChange]);
  const [selected, setSelected] = useState(0);
  const [optionPriceHolder, setOptionPriceHolder] = useState(optionPrice);
  const [itemTax, setItemTax] = useState(0);
  //Ref to change
  const optionArray = useRef(null);
  const optionArrayIdentity = menuSelectionFormRef.current
    ? (hasRecommended
      ? menuSelectionFormRef.current.recommendedArray
      : menuSelectionFormRef.current.optionArray)
    : null;

  const skipOption = ["No Dressing Please!", "No Crunches"];

  //can be recommendation or option
  useEffect(() => {
    optionArray.current = hasRecommended
      ? recommendedArray
      : menuSelectionFormRef.current.optionArray;
  }, [hasRecommended, menuSelectionFormRef, recommendedArray]);


  //Make sure state is updated
  //consist of option group & item
  useEffect(() => {
    setOptions(cloneOptions(options));
    let selected = 0;
    options.forEach((item) => { if (item.active) selected += (item.qty ? item.qty : 1); });
    setSelected(selected);
  }, [options]);

  /**
   * RECOMMENDATION HANDLING
   * ******************* */
  const recClick = (array, codeId) => {
    for (let option of recommendedArray[menuIndex].options) {
      if (option.codeId == codeId) {
        if (option.optionArray.length > 0) {
          showRecItem(JSON.parse(JSON.stringify(option)));
        } else {
          option.active = true;
          option.qty++;
          addRecItem(JSON.parse(JSON.stringify(option)));
        }
      }
    }
  };
  const recQtyAdj = (array, qty, index) => {
    if (recommendedArray[menuIndex].options[index].qty < qty) {
      //add item
      recClick(array, recommendedArray[menuIndex].options[index].codeId);
    } else {
      //remove item
      if (recommendedArray[menuIndex].options[index].qty <= 1) {
        recommendedArray[menuIndex].options[index].active = false;
        recommendedArray[menuIndex].options[index].qty = 0;
      } else {
        recommendedArray[menuIndex].options[index].qty--;
      }

      removeRecItem(recommendedArray[menuIndex].options[index].codeId);
    }
  };


  const setActiveLabelButton = (state) => {
    //Turn Active Label Off/On
    setActiveLabel(!state);
    //For Ref Form
    optionArray.current[menuIndex].optionActive = !state;
  };

  const adjustQuantity = produce((draft, qty, index) => {
   
    if (qty == 0) {
      draft[index].qty = 0;
      draft[index].active = false;
    } else {
      draft[index].qty = qty;
      draft[index].active = true;
    }

    if (hasRecommended) {
      recommendedArray = JSON.parse(JSON.stringify(recommendedArray));
      recommendedArray[menuIndex].options[index].qty = qty;
      recommendedArray[menuIndex].options[index].active = true;
      if (qty == 0) {
        recommendedArray[menuIndex].options[index].qty = 0;
        recommendedArray[menuIndex].options[index].active = false;
      }
    } else {
      menuSelectionFormRef.current.optionArray[menuIndex].options[index].qty = qty;
    }



    if (hasRecommended) {
      // recommendedArray[0].options = recommendedArray[menuIndex].options;
      setRecommendedArray(recommendedArray);
    }
  });


  //draf is the array of current selection
  const produceArrayStateSingle = produce((draft, itemOptionId, isMultiple, hasRecommended, menuIndex) => {
    //For State
    if (isReadOnly) return;
    let foundActive = draft.find((item) => item.itemOptionId == itemOptionId || item.codeId == itemOptionId);
    if (!foundActive) return;
    const shouldActivate = hasRecommended || optionRequired || !foundActive.active;

    if (!hasRecommended) {
      const clonedOptionArray = cloneOptions(menuSelectionFormRef.current.optionArray);
      menuSelectionFormRef.current = {
        ...menuSelectionFormRef.current,
        optionArray: clonedOptionArray
      };
      optionArray.current = clonedOptionArray;
    }

    draft.forEach((mElement) => {
      if (!isMultiple && !hasRecommended) mElement.active = false;
      if (isSetMeal) {
        //DE-SELECT ITEM
        mElement.optionArray && mElement.optionArray.forEach((element) => {
          element = JSON.parse(JSON.stringify(element));
          element.options.forEach((em) => {
            if (!element.isMultiple && !hasRecommended && (mElement.itemOptionId != itemOptionId || mElement.codeId != itemOptionId)
            ) {
              em.active = false;
            }


          })
        })
      }
    });
    let hasChildIndex = false;
    let isActivate = false;
    if (!hasRecommended) {
      isActivate = shouldActivate;
      foundActive.active = shouldActivate;

      if (isSetMeal && foundActive.optionArray) {
        foundActive.optionArray.forEach((element) => {
          element.options.forEach((em) => {
            if (!element.isMultiple && !hasRecommended) { em.active = false; }
            if (isActivate && element.defaultSelection && element.defaultSelection.indexOf(em.itemOptionId) != -1) {
              em.active = true;
              em.qty = 1;
            }
          })
        })
      }
    } else {
      foundActive.active = true;
      foundActive.qty = foundActive.qty ? foundActive.qty + 1 : 1;
    }
    // For Ref Form
    //reset all to false
    if (hasRecommended) {
      recommendedArray[menuIndex].options.forEach((element1) => {
        if (!isMultiple && !hasRecommended) element1.active = false;
        if (isSetMeal && element1.optionArray) {
          element1.optionArray.forEach((element) =>
            element.options.forEach((em) => {
              if (!isMultiple && !hasRecommended) em.active = false;
              // return element;
            })
          );
        }
      });
    } else {
      menuSelectionFormRef.current.optionArray[menuIndex].options.forEach((element1) => {
        if (!isMultiple && !hasRecommended) element1.active = false;
        if (isSetMeal && element1.optionArray) {
          element1.optionArray.forEach((element) => {
            element.options.forEach((em) => {
              if (!element.isMultiple && !hasRecommended) { em.active = false; }
              if (isActivate && element.defaultSelection && element.defaultSelection.indexOf(em.itemOptionId) != -1) {
                em.active = true;
                em.qty = 1;
              }
              // return element;
            });

          });
        }
      });
    }

    if (hasRecommended) {
      recommendedArray[menuIndex].options = recommendedArray[menuIndex].options.map((item) => {
        item = JSON.parse(JSON.stringify(item));
        if (item.itemOptionId == itemOptionId || item.codeId == itemOptionId) {

          if (!hasRecommended) {
            item.active = !item.active;
          } else {
            item.active = true;
            item.qty = item.qty ? item.qty + 1 : 1;
          }
        }
        return item;
      });
      optionArray.current = recommendedArray;
    } else {
      const updatedOptions = menuSelectionFormRef.current.optionArray[menuIndex].options.map((item) => {
        const updatedItem = { ...item };
        if (updatedItem.itemOptionId == itemOptionId || updatedItem.codeId == itemOptionId) {
          if (!hasRecommended) {
            updatedItem.active = shouldActivate;
            if (shouldActivate && updatedItem.optionArray && updatedItem.optionArray.length > 0) {
              hasChildIndex = true;
            }
          } else {
            updatedItem.active = true;
            updatedItem.qty = updatedItem.qty ? updatedItem.qty + 1 : 1;
          }
        }
        return updatedItem;
      });
      const updatedOptionArray = menuSelectionFormRef.current.optionArray.map((optionGroup, index) => (
        index === menuIndex
          ? { ...optionGroup, options: updatedOptions }
          : optionGroup
      ));

      menuSelectionFormRef.current = {
        ...menuSelectionFormRef.current,
        optionArray: updatedOptionArray
      };
      optionArray.current = updatedOptionArray;
    }

    let amount = 0;
    if (fixedPrice !== true && !hasRecommended) {
      if (isSetMeal !== true) {
        menuSelectionFormRef.current.optionArray[menuIndex].options.forEach((dt) => {
          if (dt.active) {
            amount += getOptionTotalPrice(dt);
          }
        })
      } else {
        menuSelectionFormRef.current.optionArray[menuIndex].options.forEach((dt) => {
          if (dt.active) {
            amount += getOptionTotalPrice(dt);

            if (dt.optionArray) {
              dt.optionArray.forEach((di) => {
                if (di.options) {

                  di.options.forEach((df) => {
                    if (df.active) {
                      amount += getOptionTotalPrice(df);
                    }
                  })
                }
              })
            }
          }
        })

      }
    }
    if (!hasRecommended) {
      setOptionPriceHolder((amount * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : menuSelectionFormRef.current.tax)) : 1)).toFixed(2));
      setOptions(JSON.parse(JSON.stringify(menuSelectionFormRef.current.optionArray[menuIndex].options)));
    }
    if (hasRecommended) {
      // recommendedArray[0].options = optionArray.current[menuIndex].options;
      setRecommendedArray(recommendedArray);
    }


  });

  const productMinusStateMulti = produce((draft, itemOptionId) => {
    // const foundActive = draft.find((item) => item.itemOptionId == itemOptionId || item.codeId == itemOptionId);
    if (isReadOnly) return;
    menuSelectionFormRef.current.optionArray[menuIndex].options = menuSelectionFormRef.current.optionArray[menuIndex].options.map((item) => {
      const updatedItem = { ...item };
      if (updatedItem.itemOptionId == itemOptionId || updatedItem.codeId == itemOptionId) {
        if (updatedItem.qty > 1) {
          updatedItem.active = true;
          updatedItem.qty--;
        } else {
          updatedItem.active = false;
          updatedItem.qty = 0;
        }
      }
      return updatedItem;
    });

    let selected = 0;
    menuSelectionFormRef.current.optionArray[menuIndex].options.forEach((item) => { if (item.active) selected += (item.qty ? item.qty : 1); });

    setSelected(selected);

    //Toggle Current Price
    let currentPrice = 0;
    menuSelectionFormRef.current.optionArray[menuIndex].options.forEach((element) => {
      if (element.active) {
        currentPrice += getOptionTotalPrice(element);
      }
    });
    if (!hasRecommended) {
      setOptions(JSON.parse(JSON.stringify(menuSelectionFormRef.current.optionArray[menuIndex].options)));
      setOptionPriceHolder((currentPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : menuSelectionFormRef.current.tax)) : 1)).toFixed(2));
    }
  });

  const produceArrayStateMulti = produce((draft, itemOptionId) => {
    if (isReadOnly) return;
    //For State
    const foundActive = draft.find((item) => item.itemOptionId == itemOptionId || item.codeId == itemOptionId);
    //Check if has reach max Qty;
    let hasNoOption = false;
    if (skipOption.indexOf(foundActive.title) > -1) {
      hasNoOption = true;
    }

    if (maxQty) {
      let selected = 0;
      draft.forEach((item) => {
        if (item.active) { selected += (item.qty ? item.qty : 1) }
      });
      if (foundActive.active && optionType != 4) selected--;
      else selected++;

      if (selected > maxQty) return;
    }

    let hasChildIndex = false;

    if (optionType != 4)
      foundActive.active = !foundActive.active;
    else {
      foundActive.active = true;
    }

    // Keep the form ref and calculation ref on the same immutable option-array instance.
    const updatedOptions = menuSelectionFormRef.current.optionArray[menuIndex].options.map((item) => {
      const updatedItem = { ...item };

      if (updatedItem.itemOptionId == itemOptionId || updatedItem.codeId == itemOptionId) {
        if (optionType != 4)
          updatedItem.active = !updatedItem.active;
        else if (updatedItem.qty) {
          updatedItem.active = true;
          updatedItem.qty++;
        } else {
          updatedItem.active = true;
          updatedItem.qty = 1;
        }
        if (!hasRecommended && updatedItem.optionArray && updatedItem.optionArray.length > 0) {
          hasChildIndex = true;
        }
      } else if (hasNoOption) {
        updatedItem.active = false;
        if (updatedItem.qty > 0)
          updatedItem.qty = 0
      } else if (!hasNoOption && skipOption.indexOf(updatedItem.title) > -1) {
        updatedItem.active = false;
        if (updatedItem.qty > 0)
          updatedItem.qty = 0
      }
      return updatedItem;
    });
    const updatedOptionArray = menuSelectionFormRef.current.optionArray.map((optionGroup, index) => (
      index === menuIndex
        ? { ...optionGroup, options: updatedOptions }
        : optionGroup
    ));

    menuSelectionFormRef.current = {
      ...menuSelectionFormRef.current,
      optionArray: updatedOptionArray
    };
    optionArray.current = updatedOptionArray;

    let selected = 0;
    updatedOptions.forEach((item) => { if (item.active) selected += (item.qty ? item.qty : 1); });

    setSelected(selected);

    //Toggle Current Price
    let currentPrice = 0;
    updatedOptions.forEach((element) => {
      if (element.active) {
        currentPrice += getOptionTotalPrice(element);
      }
    });
    setOptions(cloneOptions(updatedOptions));
    setOptionPriceHolder((currentPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : menuSelectionFormRef.current.tax)) : 1)).toFixed(2));


  });

  useEffect(() => {
    //run on mount
    let currentPrice = 0;
    //.active check
    optionArray.current[menuIndex].options.forEach((element) => {
      if (element.active) {
        if (isSetMeal == true) {
          currentPrice += (element.price ? element.price : 0);
        } else {
          currentPrice += getOptionTotalPrice(element);
        }
      }
    });

    setOptionPriceHolder(currentPrice.toFixed(2));
  }, [menuIndex, optionArray, isSetMeal]);


  useEffect(() => {
    //calculate total Price including add on
    //Run after optionPriceHolder is updated.
    if (fixedPrice !== true && menuSelectionFormRef.current) {
      const latestOptionArray = hasRecommended
        ? menuSelectionFormRef.current.recommendedArray
        : menuSelectionFormRef.current.optionArray;
      optionArray.current = latestOptionArray;

      let itemTax = hasRecommended == true
        ? menuSelectionFormRef.current.recommendedArray.tax
        : menuSelectionFormRef.current.tax;
      setItemTax(itemTax);
      let basePrice =
        hasRecommended == true
          ? menuSelectionFormRef.current.recommendedArray.price
          : menuSelectionFormRef.current.price;

      let comparePrice = 0;
      if ((hasRecommended && menuSelectionFormRef.current.recommendedArray.comparePrice) ||
        (!hasRecommended && menuSelectionFormRef.current.comparePrice)) {
        comparePrice = hasRecommended == true ? menuSelectionFormRef.current.recommendedArray.comparePrice : menuSelectionFormRef.current.comparePrice;
      } else if (menuSelectionFormRef.current.pricebook && Object.keys(menuSelectionFormRef.current.pricebook).length > 0) {
        let bp = menuSelectionFormRef.current.pricebook[Object.keys(menuSelectionFormRef.current.pricebook)[0]];

        if (bp.minQty == 1) {
          comparePrice = bp.price * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : menuSelectionFormRef.current.tax)) : 1);
        }
      }


      let addOnPrice = 0;
      latestOptionArray.forEach((element) => {
        element.options.forEach((element) => {
          if (element.active) {
            if (isSetMeal == true) {
              addOnPrice += getOptionTotalPrice(element);
              element.optionArray && element.optionArray.forEach((element) => {
                element.options.forEach((el) => {
                  if (el.active) {
                    addOnPrice += getOptionTotalPrice(el);

                  }
                });
              });
            } else {
              addOnPrice += getOptionTotalPrice(element);
            }
          }
        });
      });

      let totalPrice = basePrice + (!isNaN(addOnPrice) ? addOnPrice : 0);
      setPrice((totalPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2));
      if (setComparePrice != null) {
        setComparePrice((comparePrice + (!isNaN(addOnPrice) ? addOnPrice : 0) * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2));
      }
    }
  }, [
    optionPriceHolder,
    setPrice,
    setComparePrice,
    fixedPrice,
    isSetMeal,
    menuSelectionFormRef,
    optionArray,
    optionArrayIdentity,
    hasRecommended,
  ]);

  const optionComponentType = (optionType, menuIndex) => {
    switch (optionType) {
      case OPTION_BAR: {
        return (
          <MenuInfoOptionContainerBar>
            <ArrayBar
              height={40}
              lanIndex={lanIndex}
              isLanReverse={isLanReverse}
              array={optionsState}
              setActiveState={setOptions}
              menuIndex={menuIndex}
              isMultiple={isMultiple}
              produceArrayStateMulti={produceArrayStateMulti}
              produceNewState={produceArrayStateSingle}
            />
          </MenuInfoOptionContainerBar>
        );
      }
      case OPTION_MULTI_QTY: {
        return (
          <MenuInfoOptionContainer>
            <MultiQtyOption
              height={40}
              lanIndex={lanIndex}
              isLanReverse={isLanReverse}
              array={optionsState}
              setActiveState={setOptions}
              currencySymbol={currencySymbol}
              displayNetAmount={displayNetAmount}
              tax={tax}
              itemTax={itemTax}
              menuIndex={menuIndex}
              isMultiple={isMultiple}
              produceArrayStateMulti={produceArrayStateMulti}
              produceMinusArray={productMinusStateMulti}

            />
          </MenuInfoOptionContainer>
        )
      }
      case OPTION_DOT_SINGLE: {
        return (
          <MenuInfoOptionContainer>
            {optionsState.map((item, index) => (
              <ArrayOption
                themeContext={themeContext}
                key={item.title + index}
                /* Make clickable area bigger */
                onClick={() =>
                  setOptions(produceArrayStateSingle(optionsState, item.itemOptionId, false, false, menuIndex))
                }
              >

                <DotTextContainer themeContext={themeContext}>
                  {item.itemImage &&
                    <DotImage image={item.itemImage} />
                  }
                  {(lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ?
                    item.title :
                    item.title2 ? item.title2 : item.title}<br />
                  <DotPrice themeContext={themeContext}>{!hidePrice && (Number(item.addPrice) ? "+ " + currencySymbol + (item.addPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2) : "Free")}
                  </DotPrice>
                </DotTextContainer>
                <DotButtonContainer>
                  <DotButton
                    index={index}
                    title={(lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ?
                      item.title :
                      item.title2 ? item.title2 : item.title}
                    activeState={item.active}
                    array={optionsState}
                    setActiveState={() => { }}
                    produceNewState={() => { }}
                  />
                </DotButtonContainer>
              </ArrayOption>
            ))}
          </MenuInfoOptionContainer>
        );
      }

      case OPTION_DOT_MULTIPLE: {
        return (
          <MenuInfoOptionContainer>
            {optionsState.map((item, index) => (
              <ArrayOption
                themeContext={themeContext}
                key={item.title + index}
                /* Make clickable area bigger */
                onClick={() =>
                  setOptions(produceArrayStateMulti(optionsState, item.itemOptionId, true, menuIndex))
                }
              >

                <DotTextContainer themeContext={themeContext}>
                  {item.itemImage &&
                    <DotImage image={item.itemImage} />
                  }
                  {(lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ?
                    item.title :
                    item.title2 ? item.title2 : item.title}<br />
                  <DotPrice themeContext={themeContext}>{!hidePrice && (Number(item.addPrice) ? "+ " + currencySymbol + (item.addPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2) : "Free")}
                  </DotPrice>
                </DotTextContainer>
                <DotButtonContainer>
                  <DotButton
                    index={index}
                    title={(lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ?
                      item.title :
                      item.title2 ? item.title2 : item.title}
                    activeState={item.active}
                    array={optionsState}
                    setActiveState={() => { }}
                    produceNewState={() => { }}
                  />
                </DotButtonContainer>
              </ArrayOption>
            ))}
          </MenuInfoOptionContainer>
        );
      }

      case OPTION_CAROUSEL:
      case OPTION_SET_MEAL_2:
      case OPTION_SET_MEAL_3:
        {
          return (
            <React.Fragment>
              <Carousel
                key={"carousel"}
                visibleSlide={visibleSlide}
                isSetMeal={isSetMeal}
                isMultiple={isMultiple}
                array={optionsState}
                hasRecommended={hasRecommended}
                setArrayState={setOptions}
                produceNewState={produceArrayStateSingle}
                produceMultiState={produceArrayStateMulti}
                adjustQuantity={adjustQuantity}
                lanIndex={lanIndex}
                isLanReverse={isLanReverse}
                menuIndex={menuIndex}
                currencySymbol={currencySymbol}
                cp={cp}
                optionType={optionType}
                recommendationClick={recClick}
                recommendationQtyAdjust={recQtyAdj}
              />
              {(!isMultiple && !hasRecommended) && GetActiveForRecursion(optionsState, menuIndex)}
            </React.Fragment>
          );
        }
      default: {
        return <div>Error</div>;
      }
    }
  };

  const GetActiveForRecursion = (optionsState, parentIndex) => {
    let recursionTarget = optionsState.find((item) => item.active == true);

    if (recursionTarget) {
      return (
        <React.Fragment>
          {recursionTarget.optionArray.map((item, index) => (
            <MenuOptionsSetMenu
              onSelectionChange={onSelectionChange}
              key={"carouseloptions" + index}
              menuIndexParent={parentIndex}
              menuIndex={index}
              menuSelectionFormRef={menuSelectionFormRef}
              menuOptions={item}
              setPrice={setPrice}
              setComparePrice={setComparePrice}
              isSetMeal={isSetMeal}
              fixedPrice={fixedPrice}
              lanIndex={lanIndex}
              isLanReverse={isLanReverse}
              itemTax={itemTax}
              currencySymbol={currencySymbol}
              hasRecommended={hasRecommended}
              cp={cp}
            />
          ))}
        </React.Fragment>
      );
    } else {
      <React.Fragment />;
    }
  };

  const labelComponentType = (labelType) => (
    <OptionGroupHeading
      title={(lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ? optionTitle : (optionTitle2 || optionTitle)}
      required={optionRequired}
      minQty={minQty}
      maxQty={maxQty}
      multiple={isMultiple || optionType === OPTION_MULTI_QTY}
      selected={optionsState.reduce((total, option) => total + (option.active ? Number(option.qty || 1) : 0), 0)}
      collapsed={collapsed}
      onToggle={() => setCollapsed(value => !value)}
      price={(labelType === LABEL_PRICE || labelType === LABEL_ONLY_MULTI) && optionPriceHolder > 0 ? `${currencySymbol} ${optionPriceHolder}` : null}
      activation={labelType === LABEL_DOT ? <DotButtonContainer onClick={() => setActiveLabelButton(activeLabel)}><DotButton activeState={activeLabel} setActiveState={() => { }} /></DotButtonContainer> : null}
    />
  );

  return (
    <React.Fragment>
      {!isModal &&
        <MenuInfoHeaderContainer ref={headerRef} data-option-heading data-option-readonly={isReadOnly ? "true" : undefined} themeContext={themeContext} id={menuIndex + ""}>
          {labelComponentType(isMultiple ? 3 : labelType)}
        </MenuInfoHeaderContainer>
      }
      {activeLabel ? (
        <OptionBody data-option-body collapsed={collapsed && !isModal} aria-hidden={collapsed && !isModal} inert={collapsed && !isModal ? "" : undefined} onClickCapture={captureSelection}><OptionBodyInner>{optionComponentType(optionType, menuIndex)}</OptionBodyInner></OptionBody>
      ) : (
        <React.Fragment />
      )}
    </React.Fragment>
  );
};

export default MenuOptions;
const DotPrice = styled.div`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.3;
  color: ${props => readableText(props.themeContext.backgroundColor, props.themeContext.descriptionColor, props.themeContext.primaryColor)};
`;
const MenuInfoHeaderContainer = styled.div`
  scroll-behavior: smooth;
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding: 12px 16px;
  border-top: 1px solid ${props => props.themeContext.optionBorderColor || props.themeContext.borderColor || "#eee"};
  background: ${props => props.themeContext.optionHeaderBackgroundColor || props.themeContext.backgroundColor};
`;

const MenuInfoOptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 99%;
  padding-bottom:5px;
`;

const MenuInfoOptionContainerBar = styled.div`
  display: flex;
  box-sizing: border-box;
  width: 100%;
  padding: 18px 16px 12px;
`;

const DotImage = styled.div`
    background-image: url(${(props) => props.image});
    background-size: cover;
    background-position: center;
    float: left;
    width: 48px;
    height: 48px;
    margin-right: 8px;
`;

const DotTextContainer = styled.div`
  display: block;
  flex: 1;
  min-width: 0;
  font-family: ${props => props.themeContext.fontFamily};
  color: ${props => readableText(props.themeContext.backgroundColor, props.themeContext.fontPrimary)};
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  text-align: left;
  overflow-wrap: anywhere;
`;
const PriceContainer = styled.div`
  display: grid;
  width: 35%;
  color: ${(props) => props.themeContext.fontPrimary};
  font-size: 14px;
  font-weight: bold;
  font-family: ${(props) => props.themeContext.fontFamily};
  margin-right: 5px;

  text-align: right;
  align-items: center;
  white-space: nowrap;
`;
const DotButtonContainer = styled.div`
  display: flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: flex-end;
`;

const ArrayOption = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  width: 100%;
  min-height: 58px;
  padding: 10px 16px;
  background-color: ${props => props.themeContext.backgroundColor};
  cursor: pointer;
`;

//Basically a copy to be recursively called, the only difference is the produce functions
//*********************************************************************
//******SET MEAL OPTION MENU        ****************************** */
//******************************************************************* */
const MenuOptionsSetMenu = ({
  menuIndex,
  menuIndexParent,
  menuSelectionFormRef,
  menuOptions,
  setPrice,
  setComparePrice,
  isSetMeal,
  fixedPrice,
  hasRecommended,
  lanIndex,
  isLanReverse,
  currencySymbol,
  cp,
  itemTax,
  onSelectionChange
}) => {
  const {
    labelType,
    optionType,
    optionTitle,
    optionTitle2,
    optionPrice,
    optionActive,
    optionRequired,
    options,
    minQty,
    maxQty,
    isMultiple,
    isReadOnly
  } = menuOptions;
  const themeContext = React.useContext(ThemeContext);
  const { displayNetAmount, tax, hidePrice } = React.useContext(UserContext);
  //State
  const [activeLabel, setActiveLabel] = useState(optionActive);
  const [collapsed, setCollapsed] = useState(false);
  const [optionsState, setOptions] = useState(() => cloneOptions(options));
  const { headerRef, captureSelection } = useOptionAdvance({
    optionsState, minQty, maxQty, isMultiple, optionType, collapsed, setCollapsed,
    enabled: !isReadOnly && activeLabel
  });
  useEffect(() => {
    if (onSelectionChange) onSelectionChange();
  }, [optionsState, activeLabel, onSelectionChange]);
  const [selected, setSelected] = useState(0);
  const [optionPriceHolder, setOptionPriceHolder] = useState(
    optionPrice.toFixed(2)
  );

  //Ref to change
  const optionArray = useRef(null);
  const optionArrayIdentity = menuSelectionFormRef.current
    ? (hasRecommended
      ? menuSelectionFormRef.current.recommendedArray
      : menuSelectionFormRef.current.optionArray)
    : null;

  useEffect(() => {
    optionArray.current = hasRecommended
      ? menuSelectionFormRef.current.recommendedArray
      : menuSelectionFormRef.current.optionArray;

  }, [hasRecommended, menuSelectionFormRef]);
  useEffect(() => {
    setOptions(cloneOptions(options));
    let selected = 0;
    options.forEach((item) => { if (item.active) selected += (item.qty ? item.qty : 1); });
    setSelected(selected);
  }, [options]);

  const setActiveLabelButton = (state) => {
    const updatedOptionArray = cloneOptions(menuSelectionFormRef.current.optionArray);
    const parentOptionGroup = updatedOptionArray[menuIndexParent];
    const findOptionMenu = parentOptionGroup && parentOptionGroup.options.find(
      (item) => item.active == true
    );
    const nestedOptionGroup = findOptionMenu && findOptionMenu.optionArray
      ? findOptionMenu.optionArray[menuIndex]
      : null;

    if (!nestedOptionGroup) return;

    nestedOptionGroup.optionActive = !state;
    menuSelectionFormRef.current = {
      ...menuSelectionFormRef.current,
      optionArray: updatedOptionArray
    };
    optionArray.current = updatedOptionArray;
    setActiveLabel(!state);
  };

  const produceArrayStateSingle = produce((draft, itemOptionId) => {
    if (isReadOnly) return;
    const foundActive = draft.find((item) => item.itemOptionId == itemOptionId || item.codeId == itemOptionId);
    if (!foundActive) return;
    const shouldActivate = optionRequired || !foundActive.active;

    //For State
    draft.forEach((element) => {
      element.active = false;
    });

    foundActive.active = shouldActivate;
    // For Ref Form

    const updatedOptionArray = cloneOptions(menuSelectionFormRef.current.optionArray);
    let findOptionMenu = updatedOptionArray[menuIndexParent].options.find((item) => item.active == true);
    if (!findOptionMenu) return;

    findOptionMenu.optionArray[menuIndex].options.forEach((element) => {
      element.active = false;
    });

    const menuSelectionFoundAction = findOptionMenu.optionArray[menuIndex].options.find((item) => item.itemOptionId == itemOptionId || item.codeId == itemOptionId);
    if (!menuSelectionFoundAction) return;
    menuSelectionFoundAction.active = shouldActivate;
    menuSelectionFormRef.current = {
      ...menuSelectionFormRef.current,
      optionArray: updatedOptionArray
    };
    optionArray.current = updatedOptionArray;
    let selected = 0;
    findOptionMenu.optionArray[menuIndex].options.forEach((item) => { if (item.active) selected++; });
    setSelected(selected)
    if (fixedPrice !== true) {
      const selectedPrice = shouldActivate ? getOptionUnitPrice(foundActive) : 0;
      setOptionPriceHolder((selectedPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2));
    }


  });

  const productMinusStateMulti = produce((draft, itemOptionId) => {
    if (isReadOnly) return;
    const updatedOptionArray = cloneOptions(menuSelectionFormRef.current.optionArray);
    const parentOptionGroup = updatedOptionArray[menuIndexParent];
    const findOptionMenu = parentOptionGroup && parentOptionGroup.options.find(
      (item) => item.active == true
    );
    if (!findOptionMenu || !findOptionMenu.optionArray || !findOptionMenu.optionArray[menuIndex]) return;

    //e.g. menuIndex => shiro's noodle texture or shiro's topping
    //title => Yawa, kata or bari
    const menuSelectionFoundAction = findOptionMenu.optionArray[menuIndex].options.find(
      (item) => item.itemOptionId == itemOptionId || item.codeId == itemOptionId);
    if (!menuSelectionFoundAction) return;

    if (menuSelectionFoundAction.qty && menuSelectionFoundAction.qty > 1)
      menuSelectionFoundAction.qty--;
    else {
      menuSelectionFoundAction.qty = 0;
      menuSelectionFoundAction.active = false;
    }

    menuSelectionFormRef.current = {
      ...menuSelectionFormRef.current,
      optionArray: updatedOptionArray
    };
    optionArray.current = updatedOptionArray;

    let selected = 0;
    findOptionMenu.optionArray[menuIndex].options.forEach((item) => { if (item.active) selected += (item.qty ? item.qty : 1); });
    setSelected(selected);

    //e.g: check if Shiro's topping has additional
    let currentPrice = 0;
    if (findOptionMenu.optionArray[menuIndex].options)
      findOptionMenu.optionArray[menuIndex].options.forEach((element) => {
        if (element.active) {
          currentPrice += getOptionTotalPrice(element);
        }
      });
    if (fixedPrice !== true) {
      setOptionPriceHolder((currentPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2));
    }
    setOptions(cloneOptions(findOptionMenu.optionArray[menuIndex].options));
  });

  const produceArrayStateMulti = produce((draft, itemOptionId) => {
    if (isReadOnly) return;
    //For State
    const foundActive = draft.find((item) => item.itemOptionId == itemOptionId || item.codeId == itemOptionId);
    if (!foundActive) return;
    let selected = 0;

    if (maxQty != 0) {
      draft.forEach((item) => { if (item.active) { selected += (item.qty ? item.qty : 1) } });
      if (foundActive.active && optionType != 4) selected--;
      else selected++;

      if (selected > maxQty) return;
    }

    if (optionType != 4) {
      foundActive.active = !foundActive.active;
    } else {
      foundActive.active = true;
    }
    // For Ref Form
    //e.g. selected shiro
    const updatedOptionArray = cloneOptions(menuSelectionFormRef.current.optionArray);
    const parentOptionGroup = updatedOptionArray[menuIndexParent];
    const findOptionMenu = parentOptionGroup && parentOptionGroup.options.find(
      (item) => item.active == true
    );
    if (!findOptionMenu || !findOptionMenu.optionArray || !findOptionMenu.optionArray[menuIndex]) return;

    //e.g. menuIndex => shiro's noodle texture or shiro's topping
    //title => Yawa, kata or bari
    const menuSelectionFoundAction = findOptionMenu.optionArray[menuIndex].options.find(
      (item) => item.itemOptionId == itemOptionId || item.codeId == itemOptionId);
    if (!menuSelectionFoundAction) return;

    if (optionType != 4)
      menuSelectionFoundAction.active = !menuSelectionFoundAction.active;
    else {
      menuSelectionFoundAction.active = true;
      if (menuSelectionFoundAction.qty) menuSelectionFoundAction.qty++;
      else menuSelectionFoundAction.qty = 1;
    }

    menuSelectionFormRef.current = {
      ...menuSelectionFormRef.current,
      optionArray: updatedOptionArray
    };
    optionArray.current = updatedOptionArray;
    //e.g: check if Shiro's topping has additional
    let currentPrice = 0;

    if (findOptionMenu.optionArray[menuIndex].options)
      findOptionMenu.optionArray[menuIndex].options.forEach((element) => {
        if (element.active) {

          currentPrice += getOptionTotalPrice(element);
        }
      });

    if (fixedPrice !== true) {
      setOptionPriceHolder((currentPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2));
    }
    setOptions(cloneOptions(findOptionMenu.optionArray[menuIndex].options));

    selected = 0;
    findOptionMenu.optionArray[menuIndex].options.forEach((item) => { if (item.active) selected += (item.qty ? item.qty : 1); });
    setSelected(selected);


  });

  useEffect(() => {
    if (fixedPrice !== true && menuSelectionFormRef.current) {
      const latestOptionArray = hasRecommended
        ? menuSelectionFormRef.current.recommendedArray
        : menuSelectionFormRef.current.optionArray;
      optionArray.current = latestOptionArray;
      const parentOptionGroup = latestOptionArray && latestOptionArray[menuIndexParent];

      if (parentOptionGroup && parentOptionGroup.active == true) {
        let currentPrice = 0;
        //.active check
        parentOptionGroup.options.forEach((op) => {
          const nestedOptionGroup = op.optionArray && op.optionArray[menuIndex];
          if (!nestedOptionGroup || !nestedOptionGroup.options) return;
          nestedOptionGroup.options.forEach((element) => {
            if (element.active) {
              //if (isSetMeal == true) {
              currentPrice += getOptionTotalPrice(element);
            }
          });
        });
        setOptionPriceHolder((currentPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2));
      }
    }
  }, [menuIndexParent, menuIndex, fixedPrice, isSetMeal, hasRecommended, menuSelectionFormRef, optionArrayIdentity]);

  useEffect(() => {

    if (fixedPrice !== true) {
      const latestOptionArray = hasRecommended
        ? menuSelectionFormRef.current.recommendedArray
        : menuSelectionFormRef.current.optionArray;
      optionArray.current = latestOptionArray;

      let basePrice =
        hasRecommended == true
          ? menuSelectionFormRef.current.recommendedArray.price
          : menuSelectionFormRef.current.price;

      let comparePrice = 0;
      if ((hasRecommended && menuSelectionFormRef.current.recommendedArray.comparePrice) ||
        (!hasRecommended && menuSelectionFormRef.current.comparePrice)) {
        comparePrice = hasRecommended == true
          ? menuSelectionFormRef.current.recommendedArray.comparePrice
          : menuSelectionFormRef.current.comparePrice;

      } else if (menuSelectionFormRef.current.pricebook && Object.keys(menuSelectionFormRef.current.pricebook).length > 0) {
        let bp = menuSelectionFormRef.current.pricebook[Object.keys(menuSelectionFormRef.current.pricebook)[0]];
        if (bp.minQty == 1) {
          comparePrice = bp.price * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : menuSelectionFormRef.current.tax)) : 1);
        }
      }

      let addOnPrice = 0;

      latestOptionArray.forEach((element) => {
        //Add OptionPrice Here
        element.options.forEach((element) => {
          if (element.active) {
            //if (isSetMeal == true) {
            addOnPrice += getOptionTotalPrice(element);
            if (element.optionArray) {
              element.optionArray.forEach((element) => {
                if (element.options)
                  element.options.forEach((element) => {
                    if (element.active) {
                      addOnPrice += getOptionTotalPrice(element);
                    }
                  });
              });
            }

          }
        });
      });
      let totalPrice = basePrice + (!isNaN(addOnPrice) ? addOnPrice : 0);
      setPrice((totalPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2));
      if (setComparePrice != null) {
        setComparePrice((comparePrice + (!isNaN(addOnPrice) ? addOnPrice : 0) * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2));
      }
    } else {
      setPrice(
        hasRecommended
          ? (menuSelectionFormRef.recommendedArray.current.price * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2)
          : (menuSelectionFormRef.current.price * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2)
      );

      if ((hasRecommended && menuSelectionFormRef.current.recommendedArray.comparePrice) ||
        (!hasRecommended && menuSelectionFormRef.current.comparePrice)) {
        if (setComparePrice != null)
          setComparePrice(
            hasRecommended
              ? (menuSelectionFormRef.recommendedArray.current.comparePrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2)
              : (menuSelectionFormRef.current.comparePrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2)
          );
      }
    }
  }, [
    optionPriceHolder,
    menuSelectionFormRef,
    setPrice,
    fixedPrice,
    isSetMeal,
    hasRecommended,
    optionArray,
    optionArrayIdentity,
  ]);

  const optionComponentType = (optionType) => {
    switch (optionType) {
      case OPTION_BAR: {
        return (
          <MenuInfoOptionContainerBar>
            <ArrayBar
              key={labelType + menuIndex + "arraybar"}
              height={40}
              array={optionsState}
              lanIndex={lanIndex}
              isLanReverse={isLanReverse}
              setActiveState={setOptions}
              isMultiple={isMultiple}
              produceArrayStateMulti={produceArrayStateMulti}
              produceNewState={produceArrayStateSingle}
            />
          </MenuInfoOptionContainerBar>
        );
      }
      case OPTION_MULTI_QTY: {
        return (
          <MenuInfoOptionContainer>
            <MultiQtyOption
              height={40}
              array={optionsState}
              setActiveState={setOptions}
              lanIndex={lanIndex}
              isLanReverse={isLanReverse}
              isMultiple={isMultiple}
              currencySymbol={currencySymbol}
              displayNetAmount={displayNetAmount}
              tax={tax}
              itemTax={itemTax}
              produceArrayStateMulti={produceArrayStateMulti}
              produceMinusArray={productMinusStateMulti}

            />
          </MenuInfoOptionContainer>
        )
      }
      case OPTION_DOT_SINGLE: {
        return (
          <MenuInfoOptionContainer>
            {optionsState.map((item, index) => (
              <ArrayOption
                themeContext={themeContext}
                key={item.title + index}
                /* Make clickable area bigger */
                onClick={() =>
                  setOptions(produceArrayStateSingle(optionsState, item.itemOptionId))
                }
              >

                <DotTextContainer themeContext={themeContext}>
                  {item.itemImage &&
                    <DotImage image={item.itemImage} />
                  }
                  {(lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ? item.title : item.title2 ? item.title2 : item.title}<br />
                  <DotPrice themeContext={themeContext}>{!hidePrice && (Number(item.addPrice) ? "+ " + currencySymbol + (item.addPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2) : "Free")}
                  </DotPrice>
                </DotTextContainer>
                <DotButtonContainer>
                  <DotButton
                    index={index}
                    title={(lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ? item.title : item.title2 ? item.title2 : item.title}
                    activeState={item.active}
                    array={optionsState}
                    setActiveState={() => { }}
                    produceNewState={() => { }}
                  />
                </DotButtonContainer>
              </ArrayOption>
            ))}
          </MenuInfoOptionContainer>
        );
      }

      case OPTION_DOT_MULTIPLE:
        {
          return (
            <MenuInfoOptionContainer>
              {optionsState.map((item, index) => (
                <ArrayOption
                  themeContext={themeContext}
                  key={item.title + index}
                  /* Make clickable area bigger */
                  onClick={() =>
                    setOptions(produceArrayStateMulti(optionsState, item.itemOptionId))
                  }
                >

                  <DotTextContainer themeContext={themeContext}>
                    {item.itemImage &&
                      <DotImage image={item.itemImage} />
                    }
                    {(lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ? item.title : item.title2 ? item.title2 : item.title}<br />
                    <DotPrice themeContext={themeContext}>{!hidePrice && (Number(item.addPrice) ? "+ " + currencySymbol + (item.addPrice * (displayNetAmount ? (1 + (tax.taxMode == 0 ? tax.tax : itemTax)) : 1)).toFixed(2) : "Free")}
                    </DotPrice>
                  </DotTextContainer>
                  <DotButtonContainer>
                    <DotButton
                      index={index}
                      title={(lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ? item.title : item.title2 ? item.title2 : item.title}
                      activeState={item.active}
                      array={optionsState}
                      setActiveState={() => { }}
                      produceNewState={() => { }}
                    />
                  </DotButtonContainer>
                </ArrayOption>
              ))}
            </MenuInfoOptionContainer>
          );
        }

      default: {
        return <div>Error</div>;
      }
    }
  };

  //Option Group Label
  const labelComponentType = (labelType) => (
    <OptionGroupHeading
      title={(lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ? optionTitle : (optionTitle2 || optionTitle)}
      required={optionRequired}
      minQty={minQty}
      maxQty={maxQty}
      multiple={isMultiple || optionType === OPTION_MULTI_QTY}
      selected={optionsState.reduce((total, option) => total + (option.active ? Number(option.qty || 1) : 0), 0)}
      collapsed={collapsed}
      onToggle={() => setCollapsed(value => !value)}
      price={(labelType === LABEL_PRICE || labelType === LABEL_ONLY_MULTI) && optionPriceHolder > 0 ? `${currencySymbol} ${optionPriceHolder}` : null}
      activation={labelType === LABEL_DOT ? <DotButtonContainer onClick={() => setActiveLabelButton(activeLabel)}><DotButton activeState={activeLabel} setActiveState={() => { }} /></DotButtonContainer> : null}
    />
  );

  return (
    <React.Fragment>
      <MenuInfoHeaderContainer ref={headerRef} data-option-heading data-option-readonly={isReadOnly ? "true" : undefined} themeContext={themeContext} id={menuIndexParent + "" + menuIndex}>
        {labelComponentType(isMultiple ? 3 : labelType)}
      </MenuInfoHeaderContainer>
      {activeLabel ? (
        <OptionBody data-option-body collapsed={collapsed} aria-hidden={collapsed} inert={collapsed ? "" : undefined} onClickCapture={captureSelection}><OptionBodyInner>{optionComponentType(optionType)}</OptionBodyInner></OptionBody>
      ) : (
        <React.Fragment />
      )}
    </React.Fragment>
  );
};

const OptionGroupHeading = ({ title, required, minQty, maxQty, multiple, selected, collapsed, onToggle, price, activation }) => {
  const theme = React.useContext(ThemeContext);
  const { hidePrice } = React.useContext(UserContext);
  const minimum = Number(minQty) || (required ? 1 : 0);
  const maximum = Number(maxQty) || (multiple ? 0 : 1);
  const completed = selected >= Math.max(1, minimum) && (!maximum || selected <= maximum);
  const guidance = maximum && minimum === maximum ? `Choose ${maximum}`
    : maximum && minimum ? `Choose ${minimum}-${maximum}`
    : maximum ? `Up to ${maximum}` : minimum ? `Choose at least ${minimum}` : "Optional";
  return <>
    <GroupToggle type="button" themeContext={theme} onClick={onToggle} aria-expanded={!collapsed} aria-label={title}>
      <GroupText>
        <GroupTitle>{title}</GroupTitle>
        <GroupGuidance>
          {(required || completed) && <RequiredBadge themeContext={theme} completed={completed} role="status">
            {completed && <IoCheckmarkOutline size={12} aria-hidden="true" />}
            {completed ? "Completed" : "Required"}
          </RequiredBadge>}
          <span>{guidance}</span>
        </GroupGuidance>
      </GroupText>
      {price && !hidePrice && <GroupPrice>{price}</GroupPrice>}
      <SelectionCount themeContext={theme} aria-label={`${selected} selected${maximum ? " out of " + maximum : ""}`}>{maximum ? `${selected}/${maximum}` : selected}</SelectionCount>
      {collapsed ? <IoChevronDownOutline size={16} /> : <IoChevronUpOutline size={16} />}
    </GroupToggle>
    {activation}
  </>;
};
const GroupToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => readableText(props.themeContext.optionHeaderBackgroundColor || props.themeContext.backgroundColor, props.themeContext.optionHeaderColor, props.themeContext.fontPrimary)};
  font-family: ${props => props.themeContext.fontFamily};
  text-align: left;
  cursor: pointer;
  > svg { flex-shrink: 0; color: inherit; }
  &:focus-visible { outline: 2px solid ${props => props.themeContext.primaryColor}; outline-offset: 4px; border-radius: 4px; }
`;
const GroupText = styled.span`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
`;
const GroupTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  line-height: 1.3;
`;
const GroupGuidance = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
`;
const RequiredBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 7px;
  border-radius: 999px;
  background: ${props => props.completed ? "#23734b" : lightTint(props.themeContext.primaryColor)};
  color: ${props => props.completed ? "#fff" : readableText(lightTint(props.themeContext.primaryColor), props.themeContext.primaryColor)};
  font-size: 10px;
  font-weight: 500;
`;
const SelectionCount = styled.span`
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${props => lightTint(props.themeContext.primaryColor)};
  color: ${props => readableText(lightTint(props.themeContext.primaryColor), props.themeContext.primaryColor)};
  font-size: 11px;
  font-weight: 500;
`;
const GroupPrice = styled.span`
  font-size: 12px;
  white-space: nowrap;
`;

// Advance only after an actual selection, never for defaults loaded on mount.
const useOptionAdvance = ({ optionsState, minQty, maxQty, isMultiple, optionType, collapsed, setCollapsed, enabled }) => {
  const headerRef = useRef(null);
  const interacted = useRef(false);
  const previous = useRef("");
  const captureSelection = event => {
    if (event.target.closest("[data-option-body]") === event.currentTarget) interacted.current = true;
  };
  useEffect(() => {
    const signature = JSON.stringify(optionsState.map(option => [option.itemOptionId, option.active, option.qty]));
    const changed = previous.current !== signature;
    previous.current = signature;
    if (!enabled || collapsed || !interacted.current || !changed) return undefined;
    interacted.current = false;
    const count = optionsState.reduce((sum, option) => sum + (option.active ? Number(option.qty || 1) : 0), 0);
    const target = Number(maxQty) || (!(isMultiple || optionType === OPTION_MULTI_QTY) ? 1 : Number(minQty));
    if (!target || count < target) return undefined;
    const header = headerRef.current;
    const scope = header && header.closest("[data-option-flow]");
    if (!scope) return undefined;
    const body = header.nextElementSibling;
    const child = body && body.querySelector("[data-option-heading]:not([data-option-readonly])");
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let scrollTimer;
    const timer = window.setTimeout(() => {
      const availableHeadings = Array.from(scope.querySelectorAll("[data-option-heading]:not([data-option-readonly])"));
      const nextHeading = child || availableHeadings.slice(availableHeadings.indexOf(header) + 1).find(element => !element.closest('[aria-hidden="true"]'));
      if (!nextHeading) return;
      // Keep the final group and parents of nested choices open.
      if (!child) setCollapsed(true);
      scrollTimer = window.setTimeout(() => {
        const headings = Array.from(scope.querySelectorAll("[data-option-heading]:not([data-option-readonly])"));
        const next = child || headings.slice(headings.indexOf(header) + 1).find(element => !element.closest('[aria-hidden="true"]'));
        if (next) scrollToView(next);
      }, reduced ? 0 : 260);
    }, reduced ? 0 : 160);
    return () => { window.clearTimeout(timer); window.clearTimeout(scrollTimer); };
  }, [optionsState, minQty, maxQty, isMultiple, optionType, enabled, setCollapsed]);
  return { headerRef, captureSelection };
};
const OptionBody = styled.div`
  display: grid;
  grid-template-rows: ${props => props.collapsed ? "0fr" : "1fr"};
  opacity: ${props => props.collapsed ? 0 : 1};
  transition: grid-template-rows 240ms ease, opacity 180ms ease;
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const OptionBodyInner = styled.div`
  min-height: 0;
  overflow: hidden;
`;
